"use client";

import { useEffect } from "react";
import { pushEvent } from "@/lib/analytics-events";

/**
 * Measures the booking handoff.
 *
 * The booking itself completes on fresha.com with no cross-domain
 * measurement, so the click that leaves the site is the last moment the
 * site controls (fix plan P0-4). Rather than bolt an onClick onto ~500 CTA
 * instances, this listens once at the document root and fires on any anchor
 * resolving to fresha.com. New CTAs are covered automatically.
 *
 * Capture phase, so the event is queued before navigation starts.
 */
export function BookingHandoff() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      let host: string;
      try {
        host = new URL(anchor.href, window.location.href).hostname;
      } catch {
        return;
      }
      if (!/(^|\.)fresha\.com$/.test(host)) return;

      const isVoucher = anchor.href.includes("gift-cards");
      pushEvent({
        event: isVoucher ? "voucher_handoff" : "booking_handoff",
        booking_target: anchor.href,
        source_page: window.location.pathname,
        cta_label: anchor.textContent?.trim().slice(0, 80) || undefined,
      });
    }

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
